import React, { useMemo, useState } from 'react';
import { isEqual, cloneDeep } from 'lodash';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import ModalContractLines from '../grid/ModalContractLines';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { useColumns } from '../grid/columns';
import ControlButtons from '@/components/common/ControlButtons/ControlButtons';
import useUpdateBulkTerminate, { UpdateBulkTerminateDto } from '../hooks/useUpdateBulkTerminate';
import { isTerminateDateOutOfRenewalRange } from '../components/utils';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { validator } from '../grid/validation/validator';
import LinesCount from '@/components/common/ModalWindow/LinesCount/LinesCount';
import { ContractLineBillingCycleOutput } from '@/graphql/contracts/_generated_/contracts';

interface ITerminateContractReasonModalProps {
  onClose: () => void;
  terminateContractLines: ContractLineBillingCycleOutput[];
  contract: Contract;
  readOnly?: boolean;
}

export default function TerminateBulkUpdateModal({
  terminateContractLines,
  onClose,
  contract,
  readOnly
}: ITerminateContractReasonModalProps) {
  const [isConfirmWindowVisible, setIsConfirmWindowVisible] = useState<boolean>(false);
  const [isTerminateWarningWindowVisible, setIsTerminateWarningWindowVisible] = useState<boolean>(false);
  const { updateBulkTerminate } = useUpdateBulkTerminate();
  const columns = useColumns(terminateContractLines);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const mutableContractLineSource = useMemo(() => {
    return cloneDeep(terminateContractLines);
  }, [terminateContractLines]);

  const onCellValueChanged = () => {
    const isAtLeastOneFieldIsInvalid = mutableContractLineSource.find((cl) => {
      return !validator.isValidContractLine(cl, { contract });
    });
    const isEqualSourceDataWithGridData = isEqual(terminateContractLines, mutableContractLineSource);
    const isDisabled = isEqualSourceDataWithGridData || Boolean(isAtLeastOneFieldIsInvalid);

    setIsSaveButtonDisabled(isDisabled);
  };

  const onConfirmTerminateChange = () => {
    const terminateDateOutOfRenewalRange = mutableContractLineSource.find(cl => {
      return isTerminateDateOutOfRenewalRange(cl.statusChangeDate, contract.contractEndDate);
    });

    if (terminateDateOutOfRenewalRange) {
      setIsTerminateWarningWindowVisible(true);
    } else {
      setIsConfirmWindowVisible(true);
    }
  };
  const onConfirm = () => {
    const updatedData: UpdateBulkTerminateDto = {
      contractId: contract.contractID,
      terminateContractLines: mutableContractLineSource.map((cl) => ({
        contractLineID: cl.id,
        terminationDate: cl.statusChangeDate,
        reasonCode: (cl.statusChangeReason) as string,
        comment: cl.statusChangeComment as string,
      })),
    };

    updateBulkTerminate(updatedData);
    setIsConfirmWindowVisible(false);
    setIsTerminateWarningWindowVisible(false);
    setIsSaveButtonDisabled(true);
  };

  const onCloseTerminateWarningWindow = () => {
    setIsTerminateWarningWindowVisible(false);
  };

  const onCloseConfirmWindow = () => {
    setIsConfirmWindowVisible(false);
  };

  return (
    <>
      {isTerminateWarningWindowVisible && (
        <AgConfirmationModal
          isOpen={true}
          title="You are about to set Termination date that is out of Contract Renewal window"
          onClose={onCloseTerminateWarningWindow}
          onConfirm={onConfirm}
          closeOnConfirm={false}
          contentStyle={{ width: 680 }}
        >
          The Termination Date set for the Contract/Contract Lines are out of range of Contract Renewal.
          <br />
          Please check the Termination Date and confirm the selection.
        </AgConfirmationModal>
      )}

      {isConfirmWindowVisible && (
        /* eslint-disable max-len */
        <AgConfirmationModal
          isOpen={true}
          title="You are about to Terminate Contract/Contract Line"
          onClose={onCloseConfirmWindow}
          onConfirm={onConfirm}
          closeOnConfirm={false}
          contentStyle={{ width: 600 }}
        >
          Setting Terminate status means stopping all billing activities related to this Contract/ Contract Line. Use it only with extra precaution.
        </AgConfirmationModal>
        /* eslint-enable max-len */
      )}

      <AgModalWindow
        title="Edit Terminate Details"
        isOpen={true}
        onClose={onClose}
        contentStyle={{ width: '90vw' }}
      >
        {readOnly && (
          <div
            style={{
              padding: '10px 15px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#856404',
              marginBottom: '15px',
            }}
          >
            <strong>Note: </strong>Only users with Administrator or Full Access roles can
            edit information.
          </div>
        )}

        <LinesCount className="mb-10">
          {`${terminateContractLines.length} ${
            FormattingUtils.pluralize(terminateContractLines.length, 'Line')} Selected`}
        </LinesCount>
        <ModalContractLines
          contractLines={mutableContractLineSource}
          columns={columns}
          contract={contract}
          onCellValueChanged={onCellValueChanged}
        />

        <ControlButtons
          disabled={isSaveButtonDisabled || !!readOnly}
          onCancel={onClose}
          onConfirm={onConfirmTerminateChange}
        />
      </AgModalWindow>
    </>
  );
}
